/**
 * Shown in place of an image that doesn't exist — most often a property the
 * agent has listed before the photography is back.
 *
 * Inline SVG rather than a hosted file so it costs no request and can't itself
 * 404, and deliberately neutral (a plain frame, brand-free greys) because the
 * same fallback stands in for any missing image, not only property photos.
 */
const AWAITING_IMAGE = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" role="img">' +
    '<rect width="400" height="300" fill="#e5e7eb"/>' +
    '<g fill="none" stroke="#a3a3a3" stroke-width="6" stroke-linejoin="round">' +
    '<rect x="140" y="98" width="120" height="90" rx="6"/>' +
    '<path d="M140 162l30-26 26 22 30-30 34 34"/>' +
    '</g>' +
    '<circle cx="176" cy="126" r="9" fill="#a3a3a3"/>' +
    '<text x="200" y="228" font-family="sans-serif" font-size="18" fill="#737373" text-anchor="middle">Awaiting image</text>' +
    '</svg>',
)}`

const VIDEO_EMBED_PATTERNS = [/^https?:\/\/player\.vimeo\.com\/video\//i, /^https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\//i]

export default {
  install: (app) => {
    app.config.globalProperties.$helpers = {
      moveItemUp(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index - 1, 0, item)
      },
      moveItemDown(item, collection) {
        const index = collection.indexOf(item)
        collection.splice(index, 1)
        collection.splice(index + 1, 0, item)
      },
      /**
       * The tag for a module's main title: the admin's chosen heading level
       * (`config.heading_level`, see App\Support\HeadingLevel) or the
       * variant's own tag when none is set. Whitelisted, because
       * `<component :is>` would render any string it's handed as an element.
       *
       *   <component :is="$helpers.headingTag(config, 'h2')">…</component>
       */
      headingTag(config, fallback = 'h2') {
        return ['h1', 'h2', 'h3', 'h4'].includes(config?.heading_level) ? config.heading_level : fallback
      },
      /**
       * A level class (`h1`–`h4` / `p`) a variant puts on its title to size it
       * differently from its tag — dropped once the admin picks a level, since
       * the chosen level sets the size too.
       *
       *   :class="$helpers.headingSizeClass(config, 'h3')"
       */
      headingSizeClass(config, sizeClass) {
        return ['h1', 'h2', 'h3', 'h4'].includes(config?.heading_level) ? null : sizeClass
      },
      removeItem(item, collection) {
        const index = collection.indexOf(item)
        if (index >= 0) {
          collection.splice(collection.indexOf(item), 1)
        }
      },
      // Collapse an ordered list of { day, hours } into concise ranges, merging
      // consecutive days that share the same hours, e.g.
      //   [{day:'Monday',hours:'9-6'}, {day:'Tuesday',hours:'9-6'}, {day:'Friday',hours:'9-5'}]
      //   => [{label:'Monday to Tuesday', hours:'9-6'}, {label:'Friday', hours:'9-5'}]
      groupOpeningHours(entries) {
        if (!Array.isArray(entries)) {
          return []
        }
        const groups = []
        entries.forEach((entry) => {
          const last = groups[groups.length - 1]
          if (last && last.hours === entry.hours) {
            last.endDay = entry.day
          } else {
            groups.push({ startDay: entry.day, endDay: entry.day, hours: entry.hours })
          }
        })
        return groups.map((group) => ({
          label: group.startDay === group.endDay ? group.startDay : `${group.startDay} to ${group.endDay}`,
          hours: group.hours,
        }))
      },
      isValidUrl(url) {
        return /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(:[0-9]+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/.test(
          url,
        )
      },
      // True when a link points at an embeddable player — i.e. the output of
      // sanitiseVideoUrl(). This is the signal NtCtaButton uses to open the
      // video in a modal instead of navigating, and that variants use to style
      // such a CTA as a "watch this" action rather than a destination.
      isVideoEmbedUrl(url) {
        return !!url && VIDEO_EMBED_PATTERNS.some((pattern) => pattern.test(url))
      },
      // The address to frame when a CTA opens its link in a modal. An Issuu
      // document page refuses to be framed (X-Frame-Options: SAMEORIGIN), so
      // it is swapped for Issuu's own embed of the same document. Anything
      // else is framed as it is.
      modalEmbedUrl(url) {
        const issuu = /^https?:\/\/(?:www\.)?issuu\.com\/([^/?#]+)\/docs\/([^/?#]+)/i.exec(url || '')

        return issuu ? `https://e.issuu.com/embed.html?u=${encodeURIComponent(issuu[1])}&d=${encodeURIComponent(issuu[2])}` : url
      },
      // Turn a Vimeo/YouTube link into a player URL configured as a silent,
      // looping background: no controls, no branding, and muted — browsers
      // refuse to autoplay anything with sound. Returns null for anything else,
      // so a caller can fall back to rendering a direct file in a <video>.
      // A Vimeo `h=` privacy hash in the source URL is carried across: without
      // it, a privacy-restricted video answers the embed with a 401.
      /**
       * A still frame for a background video, used behind the player.
       *
       * An embedded player takes no `poster`, and browsers routinely decline to
       * autoplay (data saver, low power, reduced motion, a slow start). When
       * that happens the iframe shows YouTube's OWN paused state — its
       * thumbnail plus play/pause/skip controls — over the hero. Painting a
       * still frame underneath means the visitor sees the video's first frame
       * instead. Returns null for anything we can't derive one from.
       */
      videoBackgroundPosterUrl(url) {
        if (!url) {
          return null
        }

        const youtube = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/i.exec(url)

        return youtube ? `https://i.ytimg.com/vi/${youtube[1]}/maxresdefault.jpg` : null
      },
      videoBackgroundEmbedUrl(url) {
        if (!url) {
          return null
        }

        const vimeo = /^https?:\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)/i.exec(url)
        if (vimeo) {
          const params = new URLSearchParams({ background: '1', autoplay: '1', loop: '1', muted: '1', autopause: '0' })
          const hash = /[?&]h=(\w+)/i.exec(url)
          if (hash) {
            params.set('h', hash[1])
          }
          return `https://player.vimeo.com/video/${vimeo[1]}?${params}`
        }

        const youtube = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/i.exec(url)
        if (youtube) {
          // YouTube only loops a single video when it's also given as a
          // one-item playlist — `loop=1` alone silently does nothing.
          const params = new URLSearchParams({
            autoplay: '1',
            mute: '1',
            loop: '1',
            playlist: youtube[1],
            controls: '0',
            modestbranding: '1',
            playsinline: '1',
            rel: '0',
            disablekb: '1',
            // Decorative background: suppress the paused-state overlay, the
            // title/share chrome and the end-screen cards, which otherwise
            // paint play/pause and skip icons over the hero.
            fs: '0',
            iv_load_policy: '3',
            showinfo: '0',
            cc_load_policy: '0',
          })
          return `https://www.youtube.com/embed/${youtube[1]}?${params}`
        }

        return null
      },
      sanitiseVideoUrl(url) {
        let matches = /^https?:\/\/(?:www.)?(?:youtu.be\/|youtube.com\/watch\?.*v=)([a-zA-z0-9_-]+)/.exec(url)
        if (matches?.length > 0) {
          return `https://www.youtube.com/embed/${matches[1]}`
        }
        matches = /^https?:\/\/(?:www.|player.)?vimeo.com(?:\/.*\/review|\/.*\/download|\/video|\/external)?\/(\d*)/.exec(url)
        if (matches?.length > 0) {
          return `https://player.vimeo.com/video/${matches[1]}`
        }

        return url
      },
      /**
       * Resolve a contact "Email" link, which may point at a mailto: address or
       * at a web page (a contact form, a team landing page) instead.
       *
       * `url` wins when set, so a record that has been given a contact link
       * routes there; otherwise it falls back to `mailto:email`. Returns a
       * v-bind object ({href, target, rel}), so callers spread it rather than
       * setting :href themselves:
       *
       *   <a v-bind="$helpers.contactLink(member.email, member.contact_url)">
       *
       * Off-site URLs open in a new tab with rel="noopener noreferrer";
       * same-site paths (/contact) and mailto: links open in place. Returns
       * null when there is nothing to link to, so callers can v-if on it.
       */
      contactLink(email, url = null) {
        const link = typeof url === 'string' ? url.trim() : ''

        if (link) {
          // Root-relative paths stay in this tab; everything else (https://,
          // and protocol-relative //host) is treated as off-site.
          const isInternal = link.startsWith('/') && !link.startsWith('//')

          return isInternal ? { href: link } : { href: link, target: '_blank', rel: 'noopener noreferrer' }
        }

        return email ? { href: `mailto:${email}` } : null
      },
      // Returns an attribute bag to spread onto an <img> with v-bind — the caller doesn't need a
      // separate :src, loading, or sizes. `loading` is the first key so it renders before
      // `src`/`srcset` (a Firefox lazy-loading quirk).
      //
      // By default images lazy-load and `sizes` leads with `auto`, so supporting browsers pick the
      // variant from the image's actual laid-out width — this respects container-query layouts and
      // user-configurable module widths exactly, with no viewport guessing. `auto` only resolves for
      // lazy images; browsers without `auto` support ignore it and use the viewport list built from
      // smSize/mdSize/lgSize/xlSize (the image's rendered display width at each breakpoint, not the
      // viewport width). The defaults assume a full-bleed image; pass real display widths (e.g.
      // '50vw', '300px') for thumbnails or grid cards to improve the fallback.
      //
      // Pass eager=true ONLY for above-the-fold images (e.g. the hero) that should be fetched on
      // first paint. It renders loading="eager" + fetchpriority="high" and drops `auto` (which needs
      // lazy), so selection comes from the viewport list. Leave it false for everything that scrolls
      // into view, so off-screen images don't all download at once.
      //
      // IMPORTANT — `auto` and image height. While `sizes=auto` is in effect Chrome treats the image
      // as having NO intrinsic aspect ratio for layout, permanently: `naturalWidth/Height` report
      // correctly but the box does not follow them. An <img> that takes its height from its own
      // ratio (`w-full` with no aspect-* class, or one cancelled by `@4xl:aspect-auto`) therefore
      // collapses to the 300x150 CSS default object size — it renders 150px tall. Supplying a
      // fallback ratio doesn't rescue it either; `aspect-ratio: auto 1/1` renders square even after
      // the real 3:2 image has loaded. Pin a ratio/height in CSS and `auto` is fine — which is the
      // case for nearly every caller — otherwise use srcSetNatural() below.
      // For images whose HEIGHT comes from their own natural ratio — no aspect-* class, no fixed
      // height. Identical to srcSet() but without `auto` in `sizes`; see the note on `auto` above.
      srcSetNatural(url, smSize = '480px', mdSize = '768px', lgSize = '1280px', xlSize = '1536px', eager = false) {
        return this.srcSet(url, smSize, mdSize, lgSize, xlSize, eager, false)
      },
      // The "awaiting image" placeholder, exposed so a caller can reference it
      // directly (e.g. a CSS background) rather than through an <img>.
      awaitingImage: AWAITING_IMAGE,
      srcSet(url, smSize = '480px', mdSize = '768px', lgSize = '1280px', xlSize = '1536px', eager = false, allowAuto = true) {
        // No URL previously returned {}, which v-bind spreads into an <img> with
        // no `src` at all — the browser then draws its broken-image icon next to
        // the bare alt text. Hand back the placeholder instead, so a listing
        // whose photography hasn't arrived still renders a tidy card.
        if (!url) {
          return { loading: 'lazy', src: AWAITING_IMAGE }
        }

        const base = eager ? { loading: 'eager', fetchpriority: 'high' } : { loading: 'lazy' }

        if (!/^(https:\/\/cdn\.members\.nurtur\.tech|https:\/\/static-beta\.propertylogic\.net).*?\.(jpg|jpeg|png|gif)$/i.test(url)) {
          return { ...base, src: url }
        }

        const markers = ['_original.', '_hd.', '_larger.', '_large.', '_small.', '_thumb.', '.']
        const withVariant = (u, key) => {
          for (const marker of markers) {
            const i = u.lastIndexOf(marker)
            if (i !== -1) {
              return `${u.slice(0, i)}_${key}.${u.slice(i + marker.length)}`
            }
          }
          return u
        }

        const viewportSizes = [
          { size: smSize, viewport: 480 },
          { size: mdSize, viewport: 768 },
          { size: lgSize, viewport: 1280 },
          { size: xlSize, viewport: 1536 },
        ]
          .map((s, i, arr) => (i === arr.length - 1 ? s.size : `(max-width: ${s.viewport}px) ${s.size}`))
          .join(', ')

        return {
          ...base,
          src: url,
          srcset: [
            { key: 'thumb', width: 145 },
            { key: 'small', width: 250 },
            { key: 'large', width: 450 },
            { key: 'larger', width: 900 },
            { key: 'hd', width: 1920 },
          ]
            .map((i) => `${withVariant(url, i.key)} ${i.width}w`)
            .join(', '),
          sizes: eager || !allowAuto ? viewportSizes : `auto, ${viewportSizes}`,
        }
      },
    }
  },
}
